import { useCallback, useEffect, useRef } from 'react';
import { Audio, AVPlaybackSource } from 'expo-av';
import { useFocusEffect } from '@react-navigation/core';

type UseAudioOptions = {
    source: AVPlaybackSource;
    autoPlay?: boolean;
    delay?: number;
};

type Sound = {
    source?: AVPlaybackSource;
    sound?: Audio.Sound;
};

const audio: Sound = {};

export default function useAudio(options: UseAudioOptions) {
    const isFocused = useRef(false);
    const play = useCallback(async () => {
        audio.sound && (await audio.sound.unloadAsync());
        if (isFocused.current && audio.source !== options.source) {
            const audioSound = await Audio.Sound.createAsync(options.source);
            audio.sound = audioSound.sound;
            await audio.sound.playAsync();
        } else if (isFocused.current) {
            await audio.sound?.playAsync();
        }
    }, [options.source]);

    useFocusEffect(
        useCallback(() => {
            isFocused.current = true;
            return () => {
                isFocused.current = false;
            };
        }, []),
    );

    useEffect(() => {
        if (options.autoPlay) {
            setTimeout(play, options.delay || 0);
        }
        return () => {
            if (audio.sound) {
                audio.sound.unloadAsync();
                audio.sound = undefined;
            }
        };
    }, [options.autoPlay, options.delay, play]);

    return { play };
}
